import { Tag } from 'antd';
import apiFundEcontractsCall from 'apps/investment/apiCalls/fund-econtracts/list';
import { columnsGenerator as columnsGeneratorFundEcontracts } from 'apps/investment/components/fund-econtracts/config';
import { FundAccountActions } from './PostActions';
import {
  companyColors,
  identificationTypeLabels,
} from 'apps/investment/constants';
import {
  fundAccountESignStatusColors,
  fundAccountESignStatusLabels,
  fundAccountStatusColors,
} from 'apps/investment/constants/fund-account';
import { DataTable } from 'common/data-table';
import CashSVG from 'common/svg/cash-svg';
import BooleanIcons from 'common/ui/boolean-icons';
import ErrorMessage from 'common/ui/ErrorMessage';
import InfoCard from 'common/ui/info-card';
import { useFetchApiList } from 'hooks/api';
import { useEffect } from 'react';
import { LabelTooltip } from 'common/ui/label-tooltip';
import { capitalize } from 'lodash';
import { IdCardIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';

const ItemDetail = ({ item }) => {
  const { data: fundEcontracts, error: errorFundEcontracts, load: loadFundEcontracts } = useFetchApiList(
    apiFundEcontractsCall,
    { resourceName: 'data' }
  );

  useEffect(() => {
    if (item.id) loadFundEcontracts({ fund_account_id: item.id });
  }, [item.id]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <FundAccountActions record={item} />
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCard
            label="Status"
            value={
              <Tag color={fundAccountStatusColors[item.status]}>
                {item.status_label
                  ? `${item.status_label} (${item.status})`
                  : item.status}
              </Tag>
            }
          />
          <InfoCard
            label="Trading Account Manager"
            value={
              <Tag color={companyColors[item.trading_account_manager]}>
                {item.trading_account_manager}
              </Tag>
            }
          />
          <InfoCard
            label="Source"
            value={<Tag color={companyColors[item.source]}>{item.source}</Tag>}
          />
          <InfoCard label="eKYC Status" value={<Tag>{item.kyc_status}</Tag>} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Account Number" value={item.trading_code} />
          <InfoCard label="Fund Customer ID" value={item.fund_account_id} />
          <InfoCard label="FATCA Code" value={item.fatca_code} />
          <InfoCard
            label="eSign Status"
            value={
              item.sign_contract_status ? (
                <Tag
                  color={
                    fundAccountESignStatusColors[item.sign_contract_status]
                  }
                >
                  {fundAccountESignStatusLabels[item.sign_contract_status] ||
                    capitalize(item.sign_contract_status)}
                </Tag>
              ) : (
                <Tag color="yellow">{item.sign_contract_status}</Tag>
              )
            }
          />

          <InfoCard
            value={<BooleanIcons value={item.enabled_b2b} />}
            label="Enable B2B"
          />
          <InfoCard
            label="User"
            value={
              <Link to={`/investment/users/${item.user_id}`}>
                {item.user_id}
              </Link>
            }
          />
        </div>

        {/* Identification Section */}
        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <IdCardIcon className="mr-1" />
          Identification
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Full Name" value={item.full_name} />
          <InfoCard label="Email" value={item.email} />
          <InfoCard label="Phone" value={item.phone} />
          <InfoCard
            label={
              <LabelTooltip
                label="Địa chỉ thường trú"
                tooltipContent="residential_address"
              />
            }
            value={item.residential_address}
          />
          <InfoCard
            label={
              <LabelTooltip label="Địa chỉ liên hệ" tooltipContent="address" />
            }
            value={item.address}
          />
          <InfoCard label="Date of Birth" value={item.date_of_birth} />
          <InfoCard
            label="Gender"
            value={item.gender === 'male' ? 'Male' : 'Female'}
          />
          <InfoCard label="Nationality" value={capitalize(item.nationality)} />
          <InfoCard label="ID Number" value={item.id_number} />
          <InfoCard
            label="ID Type"
            value={identificationTypeLabels[item.id_type] || item.id_type}
          />
          <InfoCard label="ID Issue Date" value={item.id_issue_date} />
          <InfoCard label="Nationality ID" value={item.nationality_id} />

          <InfoCard
            className="col-span-2"
            label="ID Issue Place"
            value={item.id_issue_place}
          />
        </div>

        {/* Bank Information Section */}
        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <CashSVG className="mr-1" />
          Bank Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Bank Name" value={item.bank_name} />
          <InfoCard label="Bank Account Name" value={item.bank_account_name} />
          <InfoCard
            label="Bank Account Number"
            value={item.bank_account_number}
          />
          <InfoCard label="Bank Branch Name" value={item.bank_branch_name} />
          <InfoCard label="Bank ID" value={item.bank_id} />
        </div>

        {/* Document Images Section */}
        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <IdCardIcon className="mr-1" />
          Document Images
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoCard label="Front ID" value={item.front_image} />
          <InfoCard label="Back ID" value={item.back_image} />
          <InfoCard label="Selfie" value={item.selfie_image} />
        </div>

        {/* Notes */}
        {item.note && (
          <div className="grid grid-cols-1 gap-4">
            <InfoCard label="Notes" value={item.note} />
          </div>
        )}
      </div>

      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard
            label="Created At"
            value={formatDateTime(item.created_at)}
          />
          <InfoCard
            label="Approved At"
            value={formatDateTime(item.approved_at)}
          />

          <InfoCard
            label="Updated At"
            value={formatDateTime(item.updated_at)}
          />
          <InfoCard
            label="Fund Created At"
            value={formatDateTime(item.fund_created_at)}
          />
        </div>
      </div>
      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        {'Hợp đồng quỹ mở'}
      </h3>
      {!!errorFundEcontracts ? (
        <ErrorMessage errors={errorFundEcontracts} />
      ) : (
        <>
          <DataTable
            containerClassName="max-h-[30vh]"
            columns={columnsGeneratorFundEcontracts()}
            data={fundEcontracts || []}
          />
          <div className="flex justify-center text-base font-medium py-1">
            <Link to={`/investment/fund-econtracts?fund_account_id=${item.id}`}>
              Xem tất cả hđ
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default ItemDetail;
